import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  AlarmOffSharp,
  DeleteOutline,
  ReplaySharp,
  Search,
} from "@mui/icons-material";
import { chunk, escapeRegExp } from "lodash";
import { ReactComponent as FilterSvg } from "assets/Svgs/filter.svg";
import { useBoundStore } from "store/store";
import moment from "moment";
import i18n from "i18n/i18n";
import { orderBy as odb } from "lodash";
import { Button, message, notification } from "antd";

interface DashboardTableProps {
  filteredEquipments: any;
  onStartChange: (value: number) => void;
  onEndChange: (value: number) => void;
  propStartTime: number;
  propEndTime: number;
  allAreaEqs: any;
  tabSwitched: boolean;
}
interface Data {
  id: number;
  Zeitpunkt: string;
  description: string;
  Meldungstyp: string;
  Interner: string;
  Bereich: string;
  Gerätetyp: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order | any,
  orderBy: Key | any
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const getHighlightedText = (text: any, highlight: any) => {
  if (!highlight.trim()) {
    return <span>{text}</span>;
  }
  const regex = new RegExp(`(${escapeRegExp(highlight)})`, "gi");
  const parts = text?.split(regex);
  return (
    <span>
      {parts &&
        parts
          .filter((part: any) => part)
          .map((part: any, i: any) =>
            regex.test(part) ? (
              <span
                style={{ color: "var(--primary-color)", fontWeight: "bold" }}
                key={i}
              >
                {part}
              </span>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
    </span>
  );
};

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  lang: string;
  activeId?: any;
  setActiveId: any;
  isAdmin: boolean;
  allSelected: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const headCells: readonly HeadCell[] = [
    {
      id: "Zeitpunkt",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang)?.time_nkt,
    },
    {
      id: "Meldungstyp",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang)?.notificationtype + " (No.)",
    },
    {
      id: "Interner",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang).devicetype,
    },
    {
      id: "Bereich",
      numeric: false,
      disablePadding: true,
      label: i18n(props.lang).single_area,
    },
    {
      id: "Gerätetyp",
      numeric: true,
      disablePadding: false,
      label: i18n(props.lang).device_type,
    },
  ];

  const { order, orderBy, onRequestSort, setActiveId, isAdmin } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
      setActiveId(property);
    };

  return (
    <TableHead>
      <TableRow>
        {isAdmin && (
          <TableCell
            padding="checkbox"
            className="tableHead"
            sx={{ width: 48, pl: "16px !important", pr: "12px !important" }}
          >
            <Checkbox
              sx={{ p: 0 }}
              color="primary"
              indeterminate={props.numSelected > 0 && !props.allSelected}
              checked={props.allSelected}
              onChange={props.onSelectAllClick}
              inputProps={{ "aria-label": "select all events" }}
            />
          </TableCell>
        )}
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            className="tableHead"
            width={"30%"}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{ display: "flex", flexDirection: "row" }}
              IconComponent={FilterSvg}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const eventKey = (row: any) => `${row.eventlogNodeId}|${row.eventIndex}`;

const DashboardTable: React.FC<DashboardTableProps> = ({
  onStartChange,
  onEndChange,
  propStartTime,
  propEndTime,
  allAreaEqs,
  filteredEquipments,
  tabSwitched,
}) => {
  const lang: any = useBoundStore((state) => state.lang);
  const setEventsTimeRange: any = useBoundStore(
    (state) => state.setEventsTimeRange
  );
  const eventsTimeRange: any = useBoundStore((state) => state.eventsTimeRange);
  const startTimeStore: any = useBoundStore((state) => state.startTime);
  const endTimeStore: any = useBoundStore((state) => state.endTime);
  const [eventsToShow, setEventsToShow] = useState<any>([]);
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("id");
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const isAdmin: boolean = useBoundStore((state) => state.isAdmin);
  const deleteEvents: any = useBoundStore((state) => state.deleteEvents);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [searchText, setSearchText] = React.useState("");

  const [searchData, setSearchData] = React.useState<Data[] | []>([]);

  const [activeId, setActiveId] = React.useState("Zeitpunkt");
  const areaEqsEvents: any = useBoundStore((state) => state.areaEqsEvents);

  const emptyRows =
    page > 1 ? Math.max(0, (page) * rowsPerPage - eventsToShow.length) : 0;

  const [startTime, setStartTime] = React.useState(startTimeStore);
  const [endTime, setEndTime] = React.useState(endTimeStore);

  const getYesterdayStartTimestamp = () => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1); // Go back one day
    currentDate.setHours(0, 0, 0, 0); // Set time to 00:00:00

    return currentDate.getTime(); // Get timestamp
  };

  useEffect(() => {
    setPage(1);
  }, [tabSwitched]);

  let startTimeVar,
    endTimeVar = 0;
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  endTimeVar = currentDate.getTime();

  startTimeVar = getYesterdayStartTimestamp();

  const [startDate, setStartDate] = React.useState(
    moment(startTimeStore !== 0 ? startTimeStore : startTimeVar).format(
      "YYYY-MM-DD"
    )
  );
  const [endDate, setEndDate] = React.useState(
    moment(endTimeStore !== 0 ? endTimeStore : endTimeVar).format("YYYY-MM-DD")
  );
  const getAreaEqsEvents = useBoundStore((state) => state.getAreaEqsEvents);

  

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
   
    let sortedArray: any = [];

    switch (activeId) {
      case "Zeitpunkt":
        sortedArray = odb(array, ["date"], [order]);
        break;
      case "Meldungstyp":
        sortedArray = odb(array, ["meldungType"], [order]);
        break;
      case "Interner":
        sortedArray = odb(array, ["interner"], [order]);
        break;
      case "Bereich":
        sortedArray = odb(array, ["bereich"], [order]);
        break;
      case "Gerätetyp":
        sortedArray = odb(array, ["productionaline"], [order]);
        break;
      default:
        sortedArray = odb(array, ["date"], [order]);
    }

    return sortedArray;
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(visibleEvents.map(eventKey));
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (row: any) => {
    const key = eventKey(row);
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleCloseDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setDeleteText("");
  };

  const handleConfirmDelete = async () => {
    const selectedSet = new Set(selected);
    const payload = eventsToShow
      .filter((ev: any) => selectedSet.has(eventKey(ev)))
      .map((ev: any) => ({
        equipmentnodeid: ev.equipmentNodeId,
        eventlognodeid: ev.eventlogNodeId,
        eventindex: ev.eventIndex,
      }));
    setDeleting(true);
    try {
      await deleteEvents(payload);
      // reload with the currently applied time range
      if (allAreaEqs && allAreaEqs.length > 0) {
        await getAreaEqsEvents(
          allAreaEqs,
          startTimeStore !== 0 ? startTimeStore : getYesterdayStartTimestamp(),
          endTimeStore !== 0 ? endTimeStore : getEndtimeStamp()
        );
      }
      message.success(
        i18n(lang)?.delete_events_success?.replace(
          "{count}",
          String(payload.length)
        )
      );
      setSelected([]);
      setDeleteOpen(false);
      setDeleteText("");
    } catch (err: any) {
      console.error("Error deleting events:", err);
      const reason = err?.response?.data?.message || err?.message;
      notification.error({
        message: i18n(lang)?.delete_events_failed,
        description: Array.isArray(reason) ? reason.join(", ") : reason,
        duration: 8,
        placement: "topRight",
      });
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteText("");
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

 

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
    (window as any).blurAll();
  };

  let searchEnabled = searchText.length;

  const visibleEvents: any[] = searchEnabled ? searchData : eventsToShow;
  const allSelected =
    visibleEvents.length > 0 &&
    visibleEvents.every((ev: any) => selected.includes(eventKey(ev)));

  const handleSearch = (e: any) => {
    let text = e.target.value;
    setSearchText(text);
    if (text.length) {
      setPage(1);
      setSearchData(
        eventsToShow.filter((row: any) => {
          return (
            row.date.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
            row?.description
              ?.toLocaleLowerCase()
              .includes(text.toLocaleLowerCase()) ||
            row?.meldungType
              ?.toLocaleLowerCase()
              .includes(text.toLocaleLowerCase()) ||
            row?.interner
              ?.toLocaleLowerCase()
              .includes(text.toLocaleLowerCase()) ||
            row?.bereich
              ?.toLocaleLowerCase()
              .includes(text.toLocaleLowerCase()) ||
            row?.productionaline
              ?.toLocaleLowerCase()
              .includes(text.toLocaleLowerCase())
          );
        })
      );
    } else {
      setSearchData(eventsToShow);
    }
  };

  let paginationchunks = chunk(
    searchEnabled ? searchData : eventsToShow,
    rowsPerPage
  );

  const handleStartTime = (event: React.ChangeEvent<HTMLInputElement>) => {
   
    // Fetch data or perform other actions based on startTime change
   
    const date = new Date(event.target.value);
    date.setHours(0, 0, 0, 0); // Set time to 00:01 AM
    const timestamp = date.getTime();
    
    setStartTime(timestamp);

    setStartDate(event.target.value);
  };

  const handleEndTime = (event: any) => {
   
    const date = new Date(event.target.value);
    date.setHours(23, 59, 59, 999); // Set time to 00:01 AM
    const timestamp = date.getTime();
   
    setEndTime(timestamp);
    setEndDate(event.target.value);
  };
  const getStartTimeStamp = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Set time to 00:01 AM

    return date.getTime();
  };
  const getEndtimeStamp = () => {
    const dateEnd = new Date();
    dateEnd.setHours(23, 59, 59, 999); // Set time to 23:59 AM
    return dateEnd.getTime();
  };

  const handleLoad = () => {
    //set StartTimeStamp ,EndTimeStamp
    let outBoundStartTimeStamp,
      outBoundEndTimeStamp = 0;

    const date = new Date(startDate);
    date.setHours(0, 0, 0, 0); // Set time to 00:01 AM
    outBoundStartTimeStamp = date.getTime();

    const dateEnd = new Date(endDate);
    dateEnd.setHours(23, 59, 59, 999); // Set time to 23:59 AM
    outBoundEndTimeStamp = dateEnd.getTime();

    //setting time window to push live events
    setEventsTimeRange(true, outBoundStartTimeStamp, outBoundEndTimeStamp);

    //calling API within time period
    if (allAreaEqs && allAreaEqs.length > 0) {
      getAreaEqsEvents(
        allAreaEqs,
        outBoundStartTimeStamp,
        outBoundEndTimeStamp
      );
    }
  };

  const handleClear = () => {
    onStartChange(getStartTimeStamp());
    onEndChange(getEndtimeStamp());
    let startTimeVar,
      endTimeVar = 0;
    const currentDate = new Date();
    currentDate.setHours(23, 59, 59, 999);
    endTimeVar = currentDate.getTime();
   
    startTimeVar = getYesterdayStartTimestamp();

    setEventsTimeRange(false, startTimeVar, endTimeVar);
    setStartDate(moment(startTimeVar).format("YYYY-MM-DD"));
    setEndDate(moment(endTimeVar).format("YYYY-MM-DD"));
    setStartTime(startTimeVar);
    setEndTime(endTimeVar);

    if (allAreaEqs && allAreaEqs.length > 0) {
      getAreaEqsEvents(allAreaEqs, startTimeVar, endTimeVar);
    }
  };

  useEffect(() => {
    let startTimeVar,
      endTimeVar = 0;
    const currentDate = new Date();
    currentDate.setHours(23, 59, 59, 999);
    endTimeVar = currentDate.getTime();
    
    startTimeVar = getYesterdayStartTimestamp();

   
    if (allAreaEqs && allAreaEqs.length > 0) {
      getAreaEqsEvents(
        allAreaEqs,
        startTime === 0 ? startTimeVar : startTime,
        endTime === 0 ? endTimeVar : endTime
      );
    }
  }, [allAreaEqs]);

 

  useEffect(() => {
    //get all equipments of ares
    const filteredEquipmentNodeIds = [];
   
    for (const equipment of filteredEquipments) {
      filteredEquipmentNodeIds.push(equipment?.nodeid);
    }

    const filteredEvents = []; //_.cloneDeep(areaEqsEvents);

    for (const event of areaEqsEvents) {
      if (filteredEquipmentNodeIds.includes(event.equipmentNodeId)) {
        filteredEvents.push(event);
      }
    }
    const sortedData = odb(
      filteredEvents,
      ["timestamp", "id"],
      ["desc", "desc"]
    ); // Sort by timestamp (desc), then by id (asc)
    setEventsToShow(sortedData);
    setOrder("desc");
    // drop selections that are no longer in the list
    const keys = new Set(sortedData.map(eventKey));
    setSelected((prev) =>
      prev.some((k) => !keys.has(k)) ? prev.filter((k) => keys.has(k)) : prev
    );
  }, [areaEqsEvents, filteredEquipments]);


  return (
    <>
      <div className="dashboardTableHeader dashb">
        <div className="title">{i18n(lang).dashboard_messages}</div>
        <div className="cal1">
          <TextField
            id="date"
            label={i18n(lang)?.start_time}
            type="date"
            sx={{ minWidth: "180px" }}
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
            value={startDate}
            onChange={handleStartTime}
          />
        </div>
        <div className="cal2">
          <TextField
            id="date"
            label={i18n(lang)?.end_time}
            type="date"
            sx={{ minWidth: "180px" }}
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
            value={endDate}
            onChange={handleEndTime}
          />
        </div>
        <div style={{ marginRight: 10 }} className="event-table-button-border">
          <Button
            className="btn"
            value={i18n(lang).load}
            onClick={() => handleLoad()}
          >
            <Tooltip title={i18n(lang)?.load_event_in_time_frame}>
              <span>
                {" "}
                <ReplaySharp
                  sx={{
                    "&:hover": {
                      color: "#b21412",
                    },
                  }}
                ></ReplaySharp>
              </span>
            </Tooltip>
          </Button>
        </div>
        <span></span>
        <div style={{ marginRight: 10 }} className="event-table-button-border">
          <Button value={i18n(lang).clear} onClick={() => handleClear()}>
            <Tooltip title={i18n(lang)?.reset_time_frame}>
              <span>
                <AlarmOffSharp
                  sx={{
                    "&:hover": {
                      color: "#b21412",
                    },
                  }}
                ></AlarmOffSharp>
              </span>
            </Tooltip>
          </Button>
        </div>
        {isAdmin && (
          <div
            style={{ marginRight: 10 }}
            className="event-table-button-border"
          >
            <Button
              disabled={selected.length === 0}
              onClick={() => setDeleteOpen(true)}
            >
              <Tooltip title={i18n(lang)?.delete_events}>
                <span>
                  <DeleteOutline
                    sx={{
                      "&:hover": {
                        color: "#b21412",
                      },
                    }}
                  />
                </span>
              </Tooltip>
            </Button>
          </div>
        )}
        <div className="">
          <TextField
            id="input-with-icon-textfield"
            className="tableSearchField"
            label={i18n(lang)?.enter_search_term}
            value={searchText}
            onChange={handleSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            variant="standard"
          />
        </div>
      </div>

      <Table
        aria-labelledby="tableTitle"
        size="medium"
        className="dashboardTable"
      >
        <EnhancedTableHead
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={searchEnabled ? searchData.length : eventsToShow.length}
          lang={lang}
          setActiveId={setActiveId}
          activeId={activeId}
          isAdmin={isAdmin}
          allSelected={allSelected}
        />

        <TableBody>
          {eventsToShow.length > 0 ? (
            stableSort(
              searchEnabled ? searchData : eventsToShow,
              getComparator(order, orderBy)
            )
              .slice((page-1) * rowsPerPage, (page-1) * rowsPerPage + rowsPerPage)
              .map((row: any, index: any) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    key={index}
                    style={{
                      height:
                        document.getElementById("rowDes-" + (index + 1))
                          ?.offsetHeight + "px",
                    }}
                    className="mainRow"
                    selected={isAdmin && selected.includes(eventKey(row))}
                  >
                    {isAdmin && (
                      <TableCell
                        padding="checkbox"
                        sx={{ width: 48, pl: "16px !important", pr: "12px !important" }}
                      >
                        <Checkbox
                          sx={{ p: 0 }}
                          color="primary"
                          checked={selected.includes(eventKey(row))}
                          onChange={() => handleSelectOne(row)}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      component="th"
                      scope="row"
                      style={{
                        color: "#000000DE",
                        width: "30%",
                        position: "relative",
                        paddingTop: "10px",
                        paddingBottom:
                          row.description.length >= 150 ? "43px" : "27px",
                      }}
                      id={labelId}
                      className="mainTH"
                    >
                      <div className="rowTitle">
                        {getHighlightedText(row.date, searchText)}
                      </div>
                      <div className="rowDesc" id={"rowDes-" + index}>
                        <Tooltip
                          title={"" + row.description}
                          placement="left-start"
                        >
                          {getHighlightedText(row.description, searchText)}
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell
                      align="right"
                      className={
                        typeof row.status === "string" &&
                        row.status?.toLowerCase().includes("error")
                          ? "errorText"
                          : ""
                      }
                    >
                      <div className="rowTitle">
                        {getHighlightedText(row.meldungType, searchText)}
                      </div>
                    </TableCell>

                    <TableCell align="right">
                      <div className="rowTitle">
                        {getHighlightedText(row.interner, searchText)}
                      </div>
                    </TableCell>
                    <TableCell align="right">
                      <div className="rowTitle">
                        {getHighlightedText(row.bereich, searchText)}
                      </div>
                    </TableCell>
                    <TableCell
                      align="right"
                      className={
                        index ===
                        (searchEnabled ? searchData : eventsToShow).length - 1
                          ? "lastTableRowCell"
                          : ""
                      }
                    >
                      <div className="rowTitle">
                        {getHighlightedText(row.productionaline, searchText)}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
          ) : (
            <TableRow>
              <TableCell colSpan={isAdmin ? 6 : 5} align="center">
                {i18n(lang)?.no_data}
              </TableCell>
            </TableRow>
          )}
          {emptyRows > 0 && (
            <TableRow
              style={{
                height: 53 * emptyRows,
              }}
            >
              <TableCell colSpan={isAdmin ? 6 : 5} />
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="DashboardTablePagination">
        <div>
          {paginationchunks.length > 1 ? (
            <Pagination
              count={paginationchunks.length}
              shape="rounded"
              onChange={handleChangePage}
              page={page}
              hideNextButton={page === paginationchunks.length}
              hidePrevButton={page === 1}
            />
          ) : (
            ""
          )}
        </div>
        <div>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              label={i18n(lang)?.rows_per_page}
              className="perPageSelect"
            >
              <MenuItem value={5}>
                {5}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={5 * 2}>
                {5 * 2}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={5 * 5}>
                {5 * 5}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={5 * 10}>
                {5 * 10}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
      {isAdmin && (
        <Dialog
          open={deleteOpen}
          onClose={handleCloseDelete}
          sx={{ backdropFilter: "blur(4px)" }}
          fullWidth
        >
          <DialogTitle>{i18n(lang)?.delete_events}</DialogTitle>
          <DialogContent>
            {deleting ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "8px 0",
                }}
              >
                <CircularProgress size={24} />
                <span>{i18n(lang)?.deleting_events}</span>
              </div>
            ) : (
            <>
            <DialogContentText sx={{ mb: 2 }}>
              {i18n(lang)?.delete_events_confirm?.replace(
                "{count}",
                String(selected.length)
              )}
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              variant="standard"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
            />
            </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete} disabled={deleting}>
              {i18n(lang)?.button_cancel}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={
                deleting ||
                deleteText.trim().toLowerCase() !== "delete" ||
                selected.length === 0
              }
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <DeleteOutline fontSize="small" />
                {i18n(lang)?.delete}
              </span>
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
export default DashboardTable;
